import React from 'react';
import ChannelLabel, { ChannelIconType } from '@op/components/ChannelLabel';
import MultipleSelect, {
  IMultipleSelectType,
} from '../components/MultipleSelect';
import { MultipleSelectButton } from '../components/MultipleSelect/MultipleSelectButton';
import CategoryGroupSelect, {
  ICategoryGroupOption,
  TCategoryGroupSelectType,
} from '../widgets/CategoryGroupSelect';
import { TComponentConfig } from '../types';
import { getMultipleSelectDefaultValue } from '../utils';
import {
  dealwithCategoryGroupOptions,
  categoryDefaultValueFunc,
  categoryBackValueFunc,
} from './CreativeTrends';
import GameSelect, { TGameSelectType } from '../components/GameSelect';
import { GAME_CATEGORY } from '../constants/games';

interface IChannelOption {
  value: string;
  text: string;
  textCn: string;
}

/**
 * cond `ugc_trends_config_channels` 接口返回 `string[]`（如 `["YOUTUBE", "TIKTOK"]`），
 * 这里转成统一的 { value, text, textCn } 三件套。
 */
function dealwithCondOptions(condInfo: any, key: string) {
  const data = condInfo[key] || [];
  return data.map((item: string) => ({
    value: item,
    text: item,
    textCn: item,
  }));
}

function defaultValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { options, valueKeys: key } = props;
  const list = (options || []) as IChannelOption[];
  return getMultipleSelectDefaultValue(
    filterValue,
    key,
    list,
    list.map((item) => item.value),
  );
}

function backValueFunc(filterValue: any, props: IMultipleSelectType) {
  const { valueKeys: key, options } = props;
  const list = ((options || []) as IChannelOption[]).map((item) => item.value);
  const values = filterValue?.[key] || [];
  if (values.length === list.length) {
    return { [key]: [] };
  }
  return { [key]: values };
}

export const UGCTrendsChannel: TComponentConfig<IMultipleSelectType> = {
  Component: MultipleSelect,
  ButtonComponent: MultipleSelectButton,
  defaultValue: defaultValueFunc,
  backValue: backValueFunc,
  condDealwith: dealwithCondOptions,
  requestKey: 'ugc_trends_config_channels',
  props: {
    title: 'Channel',
    titleCn: '渠道',
    valueKeys: 'channels',
    itemRender: (item: IChannelOption) => (
      <ChannelLabel
        iconStyle={{ width: 18, height: 18 }}
        channel_name={
          (item?.value || '').toLowerCase() as ChannelIconType
        }
        isMarketing
      />
    ),
    options: [],
    searchable: true,
    listHeight: 300,
  },
};

/**
 * UGCTrendsCategory — UGC Creative Trends 类别筛选器
 *
 * 与 CTCategory 共享同一份二级分组多选实现，仅 cond 接口 key 不同：
 *  - CTCategory:        `creative_trends_config_topic_types`
 *  - UGCTrendsCategory: `ugc_trends_config_topic_types`
 *
 * 数据契约（与 CTCategory 完全一致，复用 dealwith / default / back 三件套）：
 *  - cond 接口返回 [{ value, label_en, label_zh, children: [...] }]
 *  - 内部存储 Array<{ l1: string; l2: string[] }>
 *  - 提交给 list 接口 categories: Array<{ l1, l2 }>，全选时回退为 []
 */
export const UGCTrendsCategory: TComponentConfig<TCategoryGroupSelectType> = {
  Component: CategoryGroupSelect,
  defaultValue: categoryDefaultValueFunc,
  backValue: categoryBackValueFunc,
  condDealwith: dealwithCategoryGroupOptions,
  requestKey: 'ugc_trends_config_topic_types',
  props: {
    title: 'Category',
    titleCn: '分类',
    valueKeys: 'categories',
    options: [],
    maxShow: 2,
  },
};

/**
 * region_country cond 数据转换（来源：opinion `region_country` 接口）：
 * cond 接口：[{ region_key, region_name_en, region_name_cn, country: [{ country_name_abbre, country_name_en, country_name_cn }] }]
 * widget 期望：[{ value, text, textCn, children: [{ value, text, textCn }] }]（L1=区域，L2=国家）
 *
 * 与 ADCountryRegion / opinion RegionSelect 口径一致：region_key / country_name_abbre 统一小写作为 value。
 */
function dealwithRegionCountryOptions(condInfo: any, key: string) {
  const data = condInfo[key] || [];
  const result: ICategoryGroupOption[] = data.map((region: any) => ({
    value: region.region_key?.toLocaleLowerCase?.(),
    text: region.region_name_en,
    textCn: region.region_name_cn,
    children: (region.country || []).map((country: any) => ({
      value: country.country_name_abbre?.toLocaleLowerCase?.(),
      text: country.country_name_en,
      textCn: country.country_name_cn,
    })),
  }));
  return result;
}

/**
 * UGCTrendsCountryRegion — UGC Creative Trends 详情页 国家/地区筛选器
 *
 * 与 UGCTrendsCategory 共享同一份二级分组多选 UI（CategoryGroupSelect），
 * 仅 cond 数据源与转换不同：
 *  - UGCTrendsCategory:     cond `ugc_trends_config_topic_types`（分类）
 *  - UGCTrendsCountryRegion: cond `region_country`（区域 → 国家，复用 opinion cond）
 *
 * 数据契约与 Category 完全一致（复用 default / back 三态函数）：
 *  - 内部存储 Array<{ l1: string; l2: string[] }>（l1=区域 key，l2=国家 code）
 *  - 提交给接口 countries: Array<{ l1, l2 }>，全选时回退为 []
 */
export const UGCTrendsCountryRegion: TComponentConfig<TCategoryGroupSelectType> =
{
  Component: CategoryGroupSelect,
  defaultValue: categoryDefaultValueFunc,
  backValue: categoryBackValueFunc,
  condDealwith: dealwithRegionCountryOptions,
  requestKey: 'region_country',
  props: {
    title: 'Country/Region',
    titleCn: '国家/地区',
    valueKeys: 'countries',
    options: [],
    maxShow: 2,
  },
};

/**
* 游戏过滤器配置
*
* 功能说明：
* 1. 游戏分类使用 Segment 样式平铺显示
* 2. 游戏选择使用多选下拉组件
* 3. 切换分类时自动过滤已选游戏，只保留属于当前分类的游戏
* 4. 默认选中所有游戏
*/
export const UGCGame: TComponentConfig<TGameSelectType> = {
  // 渲染组件
  Component: GameSelect,

  // 计算默认值
  defaultValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props;
    const { category, games } = valueKeys;
    return {
      [category]: GAME_CATEGORY.ALL,
      [games]: filterValue?.[games] || options.map((item) => item.game_id),
    };
  },

  // 返回值转换
  backValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props || {};
    const { category, games } = valueKeys;
    const value = filterValue?.[games] || [];

    // 全选：返回空数组
    if (value.length === options.length && value.length > 0) {
      return {
        [category]: filterValue[category],
        [games]: [],
      };
    }

    // 全不选：返回 ["-99999"]
    if (value.length === 0) {
      return {
        [category]: filterValue[category],
        [games]: ['-99999'],
      };
    }

    // 部分选择：返回选中的 games
    return {
      [category]: filterValue[category],
      [games]: value,
    };
  },
  requestKey: 'ugc_trends_config_games',
  // 组件属性
  props: {
    title: 'Games',
    titleCn: '游戏',
    valueKeys: {
      category: 'game_category',
      games: 'game_ids',
    },
    allOption: { text: 'All Games', textCn: '全部游戏', value: 'all' },
    // 注意：实际游戏选项由组件通过 getGamesByCategory 动态获取
    options: [],
    listWidth: 350,
    listHeight: 300,
    searchable: true,
    matchButtonWidth: false,
  },
};